import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Globals } from '../shared/globals';
import { AuthService } from './auth.service';
import { Headers } from './headers';
import { License } from '../models/license.model';
import { Api } from '../shared/api';
import { JsonSerializer } from 'typescript-json-serializer';
import { LicenseCoverage } from '../models/license-coverage.model';
import { LicenseCreateByUserDtoModel } from '../models/license-create-by-user-dto.model';
import { LicenseAddQuantityDtoModel } from '../models/license-add-quantity-dto.model';

export enum UpdateCredentialsResult {
  Ok = 0,
  NoChange = 1,
  SmartMeUnknownError = 2,
  EmailAlreadyExists = 3,
  InvalidPassword = 4,
  StripeConnectedAccountError = 5,
  StripeCustomerAccountError = 6,
  LocalUpdateError = 7,
  InvalidEmail = 8,
}

@Injectable({
  providedIn: 'root',
})
export class AccountService {
  private readonly baseUrl = this.globals.apiUrl('users');
  private readonly licensesUrl = this.globals.apiUrl('account/licenses');
  private readonly licenseCoverageUrl = `${this.licensesUrl}/coverage`;
  private readonly jsonSerializer = new JsonSerializer();

  constructor(
    private http: HttpClient,
    private globals: Globals,
    private authService: AuthService,
    private api: Api
  ) {}

  updateEmail(
    oldEmail: string,
    newEmail: string
  ): Promise<UpdateCredentialsResult> {
    return this.authService.getToken().then((token) => {
      return this.http
        .put(
          this.baseUrl + '/email',
          {
            oldEmail: oldEmail,
            newEmail: newEmail,
          },
          {
            headers: Headers.headers(token),
          }
        )
        .toPromise()
        .then((response) => {
          return Promise.resolve(response as UpdateCredentialsResult);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    });
  }

  updatePassword(
    oldPassword: string,
    newPassword: string
  ): Promise<UpdateCredentialsResult> {
    return this.authService.getToken().then((token) => {
      return this.http
        .put(
          this.baseUrl + '/password',
          {
            OldPassword: oldPassword,
            NewPassword: newPassword,
          },
          {
            headers: Headers.headers(token),
          }
        )
        .toPromise()
        .then((response) => {
          return Promise.resolve(response as UpdateCredentialsResult);
        })
        .catch((err) => {
          return Promise.reject(err);
        });
    });
  }

  async updateContactDetails(
    contactEmail: string,
    contactNumber: string,
    contactDescription: string
  ): Promise<any> {
    const token = await this.authService.getToken();
    return await this.http
      .put(
        this.baseUrl + '/contactDetails',
        {
          ContactEmail: contactEmail,
          ContactNumber: contactNumber,
          ContactDescription: contactDescription,
        },
        {
          headers: Headers.headers(token),
        }
      )
      .toPromise();
  }

  async getLicenseCoverage(): Promise<LicenseCoverage> {
    const result: string = await this.api.httpGet(this.licenseCoverageUrl);
    return this.jsonSerializer.deserializeObject(result, LicenseCoverage);
  }

  async buyLicense(licenseBuyDto: LicenseCreateByUserDtoModel): Promise<void> {
    return await this.api.httpPost(this.licensesUrl, licenseBuyDto);
  }

  async addLicenseQuantity(
    licenseId: string,
    addLicenseDto: LicenseAddQuantityDtoModel
  ): Promise<void> {
    return await this.api.httpPost(
      `${this.licenseUrl(licenseId)}/add-quantity`,
      addLicenseDto
    );
  }

  async cancelLicense(licenseId: string): Promise<void> {
    return await this.api.httpDelete(this.licenseUrl(licenseId));
  }

  async getLicenses(): Promise<License[]> {
    const result: string = await this.api.httpGet(this.licensesUrl);
    return this.jsonSerializer.deserializeObjectArray(result, License);
  }

  private readonly licenseUrl = (licenseId: string) =>
    `${this.licensesUrl}/${licenseId}`;
}
